import { useLocation } from "@reach/router";
import { AnimatePresence, motion } from "framer-motion";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { FC, ReactNode, useEffect, useId, useRef, useState } from "react";

import { CarrotIcon, ChevronNav } from "@theme/assets";
import { Box, Button, ThemeUIStyleObject, menuOpenAtom } from "@theme/components";
import { keyboardUserAtom, useIsTouchDevice, useKeyPress } from "@theme/utils";

interface NavLangDropdownProps {
  buttonLabel: ReactNode | string;
  iconComponent?: ReactNode;
  children: ReactNode;
  /** Optional aria-label on dropdown button */
  "aria-label"?: string;
  animationDuration?: number;
  isDropdownOpen?: boolean;
  setIsDropdownOpen?: (isOpen: boolean) => void;
  /** Adds drop shadow and prevents opening on hover  */
  hasDropShadow?: boolean;
  /** uses fixed position instead of absolute to stretch across the whole screen */
  fullWidth?: boolean;
  sx?: ThemeUIStyleObject;
  className?: string;
  isLangDropdown?: boolean;
}

/**
 * Active Dropdown ID Atom
 *
 * Used to prevent multiple dropdown menus from being opened at one time
 */
export const activeDropdownIdAtom = atom<string | null>(null);

export const DropdownMenu: FC<NavLangDropdownProps> = ({
  buttonLabel,
  iconComponent,
  children,
  animationDuration = 0.8,
  isDropdownOpen: overRideState,
  setIsDropdownOpen: setOverRideState,
  hasDropShadow = false,
  fullWidth = false,
  "aria-label": ariaLabel,
  sx,
  className,
  isLangDropdown = false,
  ...props
}) => {
  const id = useId();
  const isTouchDevice = useIsTouchDevice();
  const isKeyboardUser = useAtomValue(keyboardUserAtom);
  const setIsMenuOpen = useSetAtom(menuOpenAtom);
  const [activeDropdownId, setActiveDropdownId] = useAtom(activeDropdownIdAtom);

  const [showDropShadow, setShowDropShadow] = useState(false);

  // allow controlling open state in component or externally
  const [isOpen, setOpen] = useState(false);
  const isDropdownOpen = overRideState ?? isOpen;
  const setIsDropdownOpen = setOverRideState ?? setOpen;

  const { pathname } = useLocation();

  const dropdownButtonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIsMenuOpen(isDropdownOpen);

    if (isDropdownOpen) {
      setActiveDropdownId(id);
      if (isKeyboardUser) {
        const firstTabbableEl = dropdownMenuRef.current?.querySelector(
          "a[href]:not([disabled]), button:not([disabled])",
        ) as HTMLButtonElement | HTMLAnchorElement | null | undefined;
        if (firstTabbableEl) firstTabbableEl.focus();
      }
    }

    if (hasDropShadow === true && isDropdownOpen === false) {
      setShowDropShadow(false);
    }
  }, [hasDropShadow, id, isDropdownOpen, isKeyboardUser, setActiveDropdownId, setIsMenuOpen]);

  // Only allow one dropdown to be open
  useEffect(() => {
    if (id !== activeDropdownId) {
      setIsDropdownOpen(false);
    }
  }, [activeDropdownId, id, setIsDropdownOpen]);

  // close dropdown menu on new page change
  useEffect(() => {
    setIsDropdownOpen(false);
    setActiveDropdownId(null);
    // disable since we only want to call this on pathname changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Close dropdown menu if user presses Escape when menu is open
  const escapeKey = useKeyPress("Escape");
  useEffect(() => {
    if (escapeKey && isDropdownOpen) {
      setIsDropdownOpen(false);
      dropdownButtonRef.current?.focus();
    }
  }, [escapeKey, isDropdownOpen, setIsDropdownOpen]);

  /**
   * Handle mouse leave. when mouse leaves the dropdown, set timeout for 250ms
   * If mouse has not re-entered the dropdown, close it. this solves issue
   * with mouse leaving the dropdown when users try to navigate to a category
   * across the page.
   */
  let timer: NodeJS.Timeout | undefined;
  const handleMouseEnter = () => {
    timer && clearTimeout(timer);
    setIsDropdownOpen(true);
  };
  const handleMouseLeave = () => {
    timer = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 250);
  };

  return (
    <Box
      data-has-dropdown-menu={true}
      className={className}
      sx={{ height: "full", position: "relative", ...sx }}
      onMouseEnter={isTouchDevice || hasDropShadow ? undefined : () => handleMouseEnter()}
      onMouseLeave={isTouchDevice || hasDropShadow ? undefined : () => handleMouseLeave()}
      {...props}
    >
      <Button
        ref={dropdownButtonRef}
        aria-label={ariaLabel}
        data-dropdown-button={true}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        variant="dropdown"
        aria-expanded={isDropdownOpen}
        sx={{ px: iconComponent ? 0 : ["s100", "s200"] }}
      >
        {iconComponent ? <>{iconComponent}</> : <>{buttonLabel}</>}
        {isLangDropdown ? (
          <ChevronNav
            sx={{
              color: isDropdownOpen ? "goldPrimary" : "grey300",
              transition: "transform 0.2s ease-in-out, color 0.2s ease-in-out",
              transform: isDropdownOpen ? "rotateX(180deg)" : "none",
            }}
          />
        ) : (
          <CarrotIcon
            sx={{
              color: isDropdownOpen ? "goldPrimary" : "grey400",
              transition: "transform 0.2s ease-in-out, color 0.2s ease-in-out",
              transform: isDropdownOpen ? "rotateX(180deg)" : "none",
            }}
          />
        )}
      </Button>

      <Box
        data-dropdown-menu={true}
        className={className}
        ref={dropdownMenuRef}
        sx={{
          outline: "none",
          overflow: "hidden",
          top: "100%",
          // prevent pointer events when animating to hide (stop nav from reopening after moving mouse out of container)
          pointerEvents: isDropdownOpen ? "auto" : "none",
          boxShadow: showDropShadow ? "lg" : "none",
          ...(fullWidth
            ? { position: "fixed", left: 0, right: 0, width: "screenWidth" }
            : { position: "absolute" }),
        }}
      >
        <AnimatePresence>
          {isDropdownOpen && (
            <motion.div
              initial="hide"
              animate={isDropdownOpen ? "show" : "hide"}
              exit="hide"
              variants={{
                hide: {
                  y: "-100%",
                  opacity: 0,
                },
                show: {
                  y: 0,
                  opacity: 1,
                },
              }}
              transition={{ ease: [1, 0, 0, 1], duration: animationDuration }}
              onAnimationComplete={(animation) => {
                if (hasDropShadow === true && animation === "show") {
                  setShowDropShadow(true);
                }
              }}
              sx={{
                bg: "beige50",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                transformOrigin: "top center",
                width: "full",
                py: "s200",
              }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
};
